
import {
    EventFlowItem,
    ExtraFunctionFormData,
} from '@/model/event-flow/EventFlow';
import { Component, Prop, PropSync, Vue } from 'vue-property-decorator';
import { throttle } from 'throttle-debounce';
import { timeFormatOptions, newDateWithAddedDays } from '@/lib/date-util';

import {
    calculateMinutePos,
    calculateNumberOfMovedMinutes,
    determineDragDirection,
    addMinutesToDate,
    clampStartDateTime,
    clampEndDateTime,
    clampMovedDateRange,
    DragDirection,
} from '@/lib/helper/event-flow-helper';
import { PropType } from 'vue';

@Component
export default class GridItem extends Vue {
    static readonly activeResizeOwner: { current: GridItem | null } = {
        current: null,
    };

    private static readonly DEFAULT_STEP_ROW_HEIGHT = 48;
    private static readonly DRAG_DIRECTION_THRESHOLD = 6;

    @Prop() readonly eventFlowItem!: EventFlowItem;
    @Prop() readonly isSheet!: boolean;
    @Prop() readonly startDateTime!: Date;
    @Prop() readonly endDateTime!: Date;
    @Prop() readonly durationInMinutes!: number;
    @Prop() readonly minuteRowHeight!: number;
    @Prop() readonly step!: number;
    @Prop() readonly index!: number;

    @PropSync('extraData', {
        type: Object as PropType<ExtraFunctionFormData>,
        default: () => ({
            showTime: true,
            showBrief: true,
            showLegend: true,
            showTimeline: true,
        }),
    })
    readonly extraFunctionFormData!: ExtraFunctionFormData;

    timeFormatOptions = timeFormatOptions;
    prevClientX = 0;
    prevClientY = 0;
    throttle = 10;
    moveX?: number = undefined;
    moveY?: number = undefined;
    latestDragOverClientX: number | null = null;
    latestDragOverClientY: number | null = null;
    dragInitialClientX = 0;
    dragInitialClientY = 0;
    dragInitialStartedAt: Date | null = null;
    dragInitialEndedAt: Date | null = null;
    dragLatestMovedMinutes = 0;
    dragDirection: DragDirection = 'pending';
    resizePointerId: number | null = null;
    resizeDirection: 'start' | 'end' | null = null;
    resizeTarget: HTMLElement | null = null;
    resizeInitialClientY = 0;
    resizeInitialStartedAt: Date | null = null;
    resizeInitialEndedAt: Date | null = null;
    resizeLatestItem: EventFlowItem | null = null;
    resizeMinuteRowHeight = GridItem.DEFAULT_STEP_ROW_HEIGHT;
    resizeHasChanged = false;
    ignoreNextClick = false;
    clickResetTimer: number | null = null;

    mounted(): void {
        globalThis.addEventListener('dragover', this.onWindowDragOver);
    }

    beforeDestroy(): void {
        globalThis.removeEventListener('dragover', this.onWindowDragOver);
        this.finishResize(false);
        if (this.clickResetTimer !== null) {
            globalThis.clearTimeout(this.clickResetTimer);
        }
    }

    get rowStart(): number {
        return calculateMinutePos(
            this.eventFlowItem.startedAt,
            this.startDateTime,
            this.durationInMinutes,
            'floor',
        );
    }

    get rowEnd(): number {
        return calculateMinutePos(
            this.eventFlowItem.endedAt,
            this.startDateTime,
            this.durationInMinutes,
            'ceil',
        );
    }

    get lastLine(): number {
        return this.durationInMinutes + 1;
    }

    get isNearTop(): boolean {
        return this.rowStart - 1 < this.step * 2;
    }

    get isNearBottom(): boolean {
        return this.rowEnd - 1 > this.durationInMinutes - this.step;
    }

    get renderedMinuteRowHeight(): number {
        return this.getMinuteRowHeight(this.$el as HTMLElement);
    }

    getMinuteRowHeight(element: HTMLElement | null): number {
        const grid = element?.closest('.event-flow-grid') as HTMLElement | null;
        const rowCount = Math.max(this.durationInMinutes, 1);

        return grid?.clientHeight
            ? grid.clientHeight / rowCount
            : this.minuteRowHeight ||
                  GridItem.DEFAULT_STEP_ROW_HEIGHT / this.step;
    }

    click(item: EventFlowItem): void {
        if (this.ignoreNextClick) {
            this.ignoreNextClick = false;
            return;
        }

        this.$root.$emit('click', item);
    }

    updateItem(item: EventFlowItem): void {
        this.$root.$emit('updateItem', item);
    }

    changeMoveItem(eventFlowItem: EventFlowItem | null): void {
        this.$root.$emit('changeMoveItem', eventFlowItem);
    }

    finishMoveItem(eventFlowItem: EventFlowItem): void {
        this.$root.$emit('finishMoveItem', eventFlowItem);
    }

    itemChangedLocal(eventFlowItem: EventFlowItem, sortItems = true): void {
        this.$root.$emit('itemChangedLocal', eventFlowItem, sortItems);
    }

    onDragStart(event: DragEvent): void {
        this.latestDragOverClientX = null;
        this.latestDragOverClientY = null;
        if (event.dataTransfer) {
            event.dataTransfer.setData('text/plain', 'event-flow-item');
            event.dataTransfer.setDragImage(new Image(), 0, 0);
        }
    }

    onDragStartMovement(event: DragEvent, eventFlowItem: EventFlowItem): void {
        this.onDragStart(event);
        this.dragInitialClientX = this.getEventClientX(event);
        this.dragInitialClientY = this.getEventClientY(event);
        this.dragInitialStartedAt = new Date(eventFlowItem.startedAt);
        this.dragInitialEndedAt = new Date(eventFlowItem.endedAt);
        this.dragLatestMovedMinutes = 0;
        this.dragDirection = 'pending';
        this.moveX = this.dragInitialClientX;
        this.moveY = this.dragInitialClientY;
        this.prevClientX = this.dragInitialClientX;
        this.prevClientY = this.dragInitialClientY;
        this.changeMoveItem(eventFlowItem);
    }

    onResizePointerDown(event: PointerEvent, direction: 'start' | 'end'): void {
        if (
            event.button !== 0 ||
            !event.isPrimary ||
            this.resizePointerId !== null ||
            (GridItem.activeResizeOwner.current !== null &&
                GridItem.activeResizeOwner.current !== this)
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        GridItem.activeResizeOwner.current = this;
        this.resizePointerId = event.pointerId;
        this.resizeDirection = direction;
        this.ignoreNextClick = true;
        this.resizeInitialClientY = event.clientY;
        this.resizeInitialStartedAt = new Date(this.eventFlowItem.startedAt);
        this.resizeInitialEndedAt = new Date(this.eventFlowItem.endedAt);
        this.resizeLatestItem = { ...this.eventFlowItem };
        this.resizeHasChanged = false;

        const target = event.currentTarget as HTMLElement | null;
        this.resizeTarget = target;
        this.resizeMinuteRowHeight = this.getMinuteRowHeight(target);

        if (target?.setPointerCapture) {
            target.setPointerCapture(event.pointerId);
        }

        if (target) {
            target.addEventListener(
                'lostpointercapture',
                this.onResizeLostPointerCapture,
            );
        }

        globalThis.addEventListener(
            'pointermove',
            this.onWindowResizePointerMove,
            {
                passive: false,
            },
        );
        globalThis.addEventListener('pointerup', this.onWindowResizePointerEnd);
        globalThis.addEventListener(
            'pointercancel',
            this.onWindowResizePointerEnd,
        );
        globalThis.addEventListener('mouseup', this.onWindowResizeMouseUp);
        globalThis.addEventListener('blur', this.onWindowResizeBlur);
    }

    onWindowResizePointerMove(event: PointerEvent): void {
        if (
            GridItem.activeResizeOwner.current !== this ||
            this.resizePointerId !== event.pointerId ||
            this.resizeDirection === null ||
            this.resizeInitialStartedAt === null ||
            this.resizeInitialEndedAt === null
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const movedMinutes = calculateNumberOfMovedMinutes(
            event.clientY - this.resizeInitialClientY,
            this.resizeMinuteRowHeight,
        );
        const newEventFlowItem = { ...this.eventFlowItem };

        if (this.resizeDirection === 'start') {
            newEventFlowItem.startedAt = clampStartDateTime(
                addMinutesToDate(this.resizeInitialStartedAt, movedMinutes),
                this.startDateTime,
                this.resizeInitialEndedAt,
                1,
            );
            newEventFlowItem.endedAt = this.resizeInitialEndedAt;
        } else {
            newEventFlowItem.startedAt = this.resizeInitialStartedAt;
            newEventFlowItem.endedAt = clampEndDateTime(
                addMinutesToDate(this.resizeInitialEndedAt, movedMinutes),
                this.resizeInitialStartedAt,
                this.endDateTime,
                1,
            );
        }

        if (
            this.resizeLatestItem?.startedAt.getTime() ===
                newEventFlowItem.startedAt.getTime() &&
            this.resizeLatestItem?.endedAt.getTime() ===
                newEventFlowItem.endedAt.getTime()
        ) {
            return;
        }

        this.resizeLatestItem = newEventFlowItem;
        this.resizeHasChanged =
            newEventFlowItem.startedAt.getTime() !==
                this.resizeInitialStartedAt.getTime() ||
            newEventFlowItem.endedAt.getTime() !==
                this.resizeInitialEndedAt.getTime();
        this.itemChangedLocal(newEventFlowItem, false);
    }

    onWindowResizePointerEnd(event: PointerEvent): void {
        if (
            GridItem.activeResizeOwner.current === this &&
            this.resizePointerId === event.pointerId
        ) {
            event.preventDefault();
            event.stopPropagation();
            this.finishResize();
        }
    }

    onWindowResizeMouseUp(): void {
        if (GridItem.activeResizeOwner.current === this) {
            this.finishResize();
        }
    }

    onWindowResizeBlur(): void {
        this.finishResize();
    }

    onResizeLostPointerCapture(): void {
        this.finishResize();
    }

    finishResize(commit = true): void {
        if (
            GridItem.activeResizeOwner.current !== this ||
            this.resizePointerId === null
        ) {
            return;
        }

        const pointerId = this.resizePointerId;
        const target = this.resizeTarget;
        const itemToUpdate =
            commit && this.resizeHasChanged ? this.resizeLatestItem : null;

        if (target) {
            target.removeEventListener(
                'lostpointercapture',
                this.onResizeLostPointerCapture,
            );
        }

        globalThis.removeEventListener(
            'pointermove',
            this.onWindowResizePointerMove,
        );
        globalThis.removeEventListener(
            'pointerup',
            this.onWindowResizePointerEnd,
        );
        globalThis.removeEventListener(
            'pointercancel',
            this.onWindowResizePointerEnd,
        );
        globalThis.removeEventListener('mouseup', this.onWindowResizeMouseUp);
        globalThis.removeEventListener('blur', this.onWindowResizeBlur);

        if (target?.hasPointerCapture?.(pointerId)) {
            target.releasePointerCapture(pointerId);
        }

        this.resizePointerId = null;
        this.resizeDirection = null;
        this.resizeTarget = null;
        this.resizeInitialStartedAt = null;
        this.resizeInitialEndedAt = null;
        this.resizeLatestItem = null;
        this.resizeHasChanged = false;
        GridItem.activeResizeOwner.current = null;

        if (this.clickResetTimer !== null) {
            globalThis.clearTimeout(this.clickResetTimer);
        }
        this.clickResetTimer = globalThis.setTimeout(() => {
            this.ignoreNextClick = false;
            this.clickResetTimer = null;
        }, 0);

        if (itemToUpdate) {
            this.updateItem(itemToUpdate);
        }
    }

    onDragEndMovement(item: EventFlowItem): void {
        this.moveX = undefined;
        this.moveY = undefined;
        this.finishMoveItem(item);
        this.dragInitialStartedAt = null;
        this.dragInitialEndedAt = null;
        this.dragLatestMovedMinutes = 0;
        this.dragDirection = 'pending';
    }

    isMoved(offsetX: number, offsetY: number): boolean {
        if (offsetX !== this.moveX || offsetY !== this.moveY) {
            this.moveX = offsetX;
            this.moveY = offsetY;
            return true;
        }

        return false;
    }

    throttleMoveItem = throttle(this.throttle, false, this.moveItem);

    moveItem(event: DragEvent, eventFlowItem: EventFlowItem): void {
        const clientX = this.getEventClientX(event);
        const clientY = this.getEventClientY(event);

        if (
            !this.isMoved(clientX, clientY) ||
            this.dragInitialStartedAt === null ||
            this.dragInitialEndedAt === null
        ) {
            return;
        }

        this.dragDirection = determineDragDirection(
            this.dragDirection,
            clientX - this.dragInitialClientX,
            clientY - this.dragInitialClientY,
            GridItem.DRAG_DIRECTION_THRESHOLD,
        );
        this.prevClientX = clientX;
        this.prevClientY = clientY;

        if (this.dragDirection !== 'vertical') {
            return;
        }

        const movedMinutes = calculateNumberOfMovedMinutes(
            clientY - this.dragInitialClientY,
            this.renderedMinuteRowHeight,
        );

        if (movedMinutes === this.dragLatestMovedMinutes) {
            return;
        }

        this.dragLatestMovedMinutes = movedMinutes;

        const newEventFlowItem = { ...eventFlowItem };

        const movedDateRange = clampMovedDateRange(
            addMinutesToDate(this.dragInitialStartedAt, movedMinutes),
            addMinutesToDate(this.dragInitialEndedAt, movedMinutes),
            this.startDateTime,
            this.endDateTime,
        );

        newEventFlowItem.startedAt = movedDateRange.startedAt;
        newEventFlowItem.endedAt = movedDateRange.endedAt;

        this.itemChangedLocal(newEventFlowItem);
    }

    onWindowDragOver(event: DragEvent): void {
        if (event.clientX !== 0) {
            this.latestDragOverClientX = event.clientX;
        }
        if (event.clientY !== 0) {
            this.latestDragOverClientY = event.clientY;
        }
    }

    getEventClientX(event: DragEvent): number {
        if (event.clientX !== 0) {
            return event.clientX;
        }

        if (this.latestDragOverClientX !== null) {
            return this.latestDragOverClientX;
        }

        return this.prevClientX;
    }

    getEventClientY(event: DragEvent): number {
        if (event.clientY !== 0) {
            return event.clientY;
        }

        if (this.latestDragOverClientY !== null) {
            return this.latestDragOverClientY;
        }

        return this.prevClientY;
    }

    canMoveTop(eventFlowItem: EventFlowItem): boolean {
        return (
            eventFlowItem.startedAt >= this.startDateTime &&
            eventFlowItem.startedAt.getDate() === this.startDateTime.getDate()
        );
    }

    canMoveBottom(eventFlowItem: EventFlowItem): boolean {
        return (
            (eventFlowItem.endedAt <= this.endDateTime &&
                eventFlowItem.endedAt.getDate() ===
                    this.endDateTime.getDate()) ||
            (eventFlowItem.endedAt <= this.endDateTime &&
                eventFlowItem.endedAt.getDate() ===
                    newDateWithAddedDays(this.endDateTime, -1).getDate()) ||
            eventFlowItem.endedAt.getHours() === 0
        );
    }
}
